/**
 * /tools/heros-journey?clientId=X — STP-046.
 *
 * Step 9 narrative — the client tells the story of the 12-week journey.
 * Persists as versioned `client_documents` doc_type=`heros_journey_narrative`
 * at step_number=9 (R1.3 Discovery Call uses the same doc_type at
 * step_number=0 — step number distinguishes).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface FormState {
  startingPoint: string;
  trialsConfronted: string;
  thresholdMoment: string;
  whoIBecame: string;
  whatsNext: string;
  coachObservations: string;
}

const DEFAULT_FORM: FormState = {
  startingPoint: '',
  trialsConfronted: '',
  thresholdMoment: '',
  whoIBecame: '',
  whatsNext: '',
  coachObservations: '',
};

export function HerosJourneyPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'heros-journey');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (typeof p.startingPoint === 'string') next.startingPoint = p.startingPoint;
          if (typeof p.trialsConfronted === 'string') next.trialsConfronted = p.trialsConfronted;
          if (typeof p.thresholdMoment === 'string') next.thresholdMoment = p.thresholdMoment;
          if (typeof p.whoIBecame === 'string') next.whoIBecame = p.whoIBecame;
          if (typeof p.whatsNext === 'string') next.whatsNext = p.whatsNext;
          if (typeof p.coachObservations === 'string') next.coachObservations = p.coachObservations;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canSubmit =
    !submitting &&
    hydrated &&
    form.startingPoint.trim().length > 0 &&
    form.trialsConfronted.trim().length > 0 &&
    form.whoIBecame.trim().length > 0;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.herosJourney.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      startingPoint: form.startingPoint.trim(),
      trialsConfronted: form.trialsConfronted.trim(),
      ...(form.thresholdMoment.trim() ? { thresholdMoment: form.thresholdMoment.trim() } : {}),
      whoIBecame: form.whoIBecame.trim(),
      ...(form.whatsNext.trim() ? { whatsNext: form.whatsNext.trim() } : {}),
      ...(form.coachObservations.trim()
        ? { coachObservations: form.coachObservations.trim() }
        : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'heros-journey',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.herosJourney.errors.generic'));
      } else {
        setSubmitError(t('tools.herosJourney.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="heros-journey-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.herosJourney.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.herosJourney.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.herosJourney.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.herosJourney.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.herosJourney.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {(
          [
            'startingPoint',
            'trialsConfronted',
            'thresholdMoment',
            'whoIBecame',
            'whatsNext',
            'coachObservations',
          ] as const
        ).map((key) => {
          const required =
            key === 'startingPoint' || key === 'trialsConfronted' || key === 'whoIBecame';
          return (
            <article key={key} className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
              <label className="block">
                <span className="form-label">
                  {t(`tools.herosJourney.fields.${key}`)}
                  {!required && (
                    <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                      {t('tools.herosJourney.optional')}
                    </span>
                  )}
                </span>
                <p className="text-xs text-gray-1 mb-s2 italic">
                  {t(`tools.herosJourney.hints.${key}`)}
                </p>
                <textarea
                  value={form[key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder={t(`tools.herosJourney.placeholders.${key}`)}
                  className="input-field mt-s2 min-h-32"
                  data-testid={`heros-journey-${key}`}
                  rows={5}
                />
              </label>
            </article>
          );
        })}

        {submitError && (
          <p role="alert" className="form-error" data-testid="heros-journey-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="heros-journey-saved-at">
            ✓ {t('tools.herosJourney.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.herosJourney.submitting') : t('tools.herosJourney.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
